package ai.wagemix.web.screens

import ai.wagemix.net.models.WMAuthFailure
import ai.wagemix.net.models.WMBadge
import ai.wagemix.web.components.WMMixCardStrings
import ai.wagemix.web.components.WMOccupationRowStrings
import ai.wagemix.web.components.WMTreeRowStrings
import ai.wagemix.web.components.WMUploadDropzoneStrings
import ai.wagemix.web.components.WMUploadRowStrings
import ai.wagemix.web.components.WMFilePreviewStrings

internal fun englishWebStrings() = WMWebStrings(
    common = WMWebStrings.Common(
        cancel = "Cancel",
        close = "Close",
        save = "Save",
        delete = "Delete",
        retry = "Try again",
        dismiss = "Not now",
        signIn = "Log in",
        register = "Create account",
        openApp = "Open app",
        viewPlans = "View plans",
        upgradeToPro = "Upgrade to Pro",
        loading = "Loading…",
        annual = "Annual",
        hourly = "Hourly",
        menu = "Menu",
        closeMenu = "Close menu",
        dataSource = "Source: U.S. Bureau of Labor Statistics, Occupational Employment and Wage Statistics.",
        projectedNote = "Shaded years are projected with a linear trend from the latest BLS release.",
        free = "Free",
        basic = "Basic",
        pro = "Pro"
    ),
    shell = WMWebStrings.Shell(
        mixBuilder = "Mix builder",
        savedMixes = "Saved mixes",
        sharedLinks = "Shared links",
        jobDescriptions = "Job descriptions",
        methodology = "Methodology",
        account = "Account",
        newMix = "New mix",
        yourMixes = "Your mixes",
        logOut = "Log out",
        unsavedMix = "Unsaved mix",
        planFree = "Free plan",
        planFreeText = "Upgrade to adjust weights, filter by area and build mixes from job descriptions.",
        planBasicText = { used, quota -> "$used of $quota job description uploads used" },
        planProText = "Unlimited job description uploads.",
        upgrade = "Upgrade",
        theme = "Theme"
    ),
    mixBuilder = WMWebStrings.MixBuilder(
        title = "Mix builder",
        aiMix = "AI mix",
        aiMixTitle = "Built by AI from your job description",
        aiMixMessage = "Wagemix matched the job description's duties to BLS occupations and weighted them by how much " +
            "of the role each covers. Adjust the occupations and weights, or re-evaluate to run the analysis again.",
        reevaluatingTitle = "Re-evaluating this mix",
        reevaluatingMessage = "The AI is reading the job description again. You can change this mix once it " +
            "finishes; it updates here automatically.",
        reevaluating = "Re-evaluating…",
        occupations = "Occupations",
        addOccupation = "Add occupation",
        emptyTitle = "Start with an occupation",
        emptyMessage = "Add one or more BLS occupations and weight them to describe the role.",
        total = "Total",
        filters = "Filters",
        area = "Area",
        industry = "Industry",
        anyArea = "National",
        anyIndustry = "All industries",
        noFilterMatches = "No matches",
        clearFilters = "Clear",
        filtersLocked = "Filters are a Basic and Pro feature.",
        filtersExclusive = "Filter by an area or an industry, not both. Clear one to choose the other.",
        filtersNeedPlan = "Area and industry filters need Basic or Pro",
        filtersNeedPlanWith = { filter -> "$filter · filters need Basic or Pro" },
        premium = "Premium",
        moreActions = "More actions",
        weightsLocked = "Weights are a Basic and Pro feature.",
        median = "Median",
        middleHalf = "Middle 50% (P25–P75)",
        typicalRange = "Typical range (P10–P90)",
        forYear = "for",
        chartTitle = "Pay range by year",
        chartDescription = "Weighted percentile wages of the mix by year",
        projection = "Projection",
        projectionMore = "Project one more year",
        projectionLess = "Project one fewer year",
        dataQualityTitle = "About this data",
        backfilled = "Some years were missing in the BLS data and were estimated by linear regression; an occupation with a single year of data is held flat.",
        projected = { latestReleaseYear -> "The current year is projected because the latest BLS release covers $latestReleaseYear. The BLS usually publishes each year's figures in May of the following year." },
        capped = "Some wages are capped in the BLS release; the top of the range may be understated.",
        missing = "Some percentiles are missing for parts of this mix.",
        occupationNoData = "No BLS data, so not part of the mix",
        occupationNoDataForFilter = "No data for this filter, so not part of the mix",
        occupationEstimated = { years, basedOn ->
            val missing = "No BLS data for ${years.joinToString(", ")}"
            if (basedOn.size == 1) {
                "$missing; held at the ${basedOn.single()} figures"
            } else {
                "$missing; estimated by linear regression from ${basedOn.joinToString(", ")}"
            }
        },
        saveMix = "Save mix",
        updateMix = "Update mix",
        share = "Share",
        discard = "Discard",
        breakdown = "Breakdown",
        occupationColumn = "Occupation",
        shareColumn = "Share",
        medianColumn = "Median",
        noStats = "Add occupations to see the pay range.",
        noDataForFilters = "BLS publishes no data for this mix in the selected area and industry. Try a broader area or industry.",
        tryTitle = "Try a mix",
        tryLead = "Start from all occupations, then add the ones you need; create a free account to save and share.",
        loggedOutTitle = "Save and share your mixes",
        loggedOutMessage = "Create a free account to save this mix, share it, and unlock weights and filters with a plan.",
        freeTitle = "Unlock the full builder",
        freeMessage = "Basic and Pro adjust weights, filter by area and industry, and build mixes from job descriptions.",
        tutorial = WMWebStrings.MixBuilderTutorial(
            progress = { step, count -> "Step $step of $count" },
            skip = "Skip tour",
            next = "Next",
            done = "Done",
            addOccupationsTitle = "Start by adding occupations",
            addOccupationsMessage = "The chart shows all occupations until you choose the BLS occupations that " +
                "make up the job. Add one or more to build a mix.",
            addOccupationsAction = "Add occupations",
            chartDataTitle = "Explore the pay range",
            chartDataMessage = "Hover over the chart, or tap it on a phone, to see the wages behind each year.",
            chartModeTitle = "Annual or hourly",
            chartModeMessage = "Switch the chart and the figures above it between annual salaries and hourly wages.",
            chartProjectionTitle = "Extend the projection",
            chartProjectionMessage = "Use − and + to project fewer or more years past the latest BLS release."
        ),
        row = WMOccupationRowStrings(
            includeInMix = "Include in mix",
            remove = "Remove",
            weightOf = "Weight of",
            lockWeight = "Lock weight",
            unlockWeight = "Unlock weight",
            upgradeToAdjust = "Upgrade to adjust"
        ),
        saveDialogTitle = "Save mix",
        saveDialogDescription = "Save this mix to compare it later or share it.",
        mixTitle = "Mix title",
        mixTitleHelper = "Shown on your saved mixes and share links.",
        updateDialogTitle = "Update mix",
        updateDialogDescription = "Update the saved mix with these changes, or save them as a new mix.",
        saveAsNew = "Save as new",
        discardDialogTitle = "Discard changes?",
        discardDialogDescription = "Your changes since the mix was last saved will be undone.",
        premiumTitle = "Included with Basic and Pro",
        premiumWeights = "Adjusting and locking weights is included with the Basic and Pro plans.",
        premiumFilters = "Area and industry filters are included with the Basic and Pro plans.",
        premiumSave = "Saving mixes is included with the Basic and Pro plans.",
        premiumShare = "Sharing mixes is included with the Basic and Pro plans.",
        premiumAiMix = "Building a mix from a job description is included with the Basic and Pro plans.",
        signInTitle = "Log in to continue",
        signInMessage = "Create a free account or log in to save, share and customise mixes."
    ),
    picker = WMWebStrings.Picker(
        title = "Add occupations",
        searchPlaceholder = "Search occupations",
        searchNotice = "Don't enter personal information.",
        selected = "selected",
        clear = "Clear",
        addToMix = "Add to mix",
        noResults = "No occupations match.",
        loadFailed = "The occupation list could not be loaded.",
        tooManyOccupationsTitle = { limit -> "A mix can have up to $limit occupations" },
        tooManyOccupationsMessage = { attempted -> "This would make $attempted. Select fewer, or remove some from the mix first." },
        row = WMTreeRowStrings(
            expand = "Expand",
            collapse = "Collapse",
            inMix = "In mix",
            parentSelected = "Included with parent",
            occupations = { count -> if (count == 1) "1 occupation" else "$count occupations" },
            matchingOccupations = { count -> if (count == 1) "1 matching occupation" else "$count matching occupations" },
        )
    ),
    savedMixes = WMWebStrings.SavedMixes(
        title = "Saved mixes",
        all = "All",
        fromJobDescriptions = "From job descriptions",
        searchPlaceholder = "Search mixes",
        emptyTitle = "No saved mixes yet",
        emptyMessage = "Build a mix and save it to compare roles over time.",
        noMatches = "No mixes match.",
        open = "Open",
        share = "Share",
        rename = "Rename",
        duplicate = "Duplicate",
        delete = "Delete",
        more = "More",
        occupations = "occupations",
        medianCaption = "Median this year",
        card = WMMixCardStrings(fromJobDescription = "AI", loading = "Loading…", sparklineOf = "Median by year for"),
        renameTitle = "Rename mix",
        deleteTitle = "Delete mix?",
        deleteDescription = "This mix and its share links will stop working."
    ),
    jobDescriptions = WMWebStrings.JobDescriptions(
        title = "Job descriptions",
        uploadFile = "Upload file",
        pasteUrl = "Paste URL",
        urlPlaceholder = "https://jobs.lever.co/…",
        analyze = "Analyze",
        supportedBoards = "Supported job boards",
        privacyNote = "Remove personal information (yours or anyone else's) before you upload. We don't check files " +
            "or pages for it.",
        privacyPolicy = "Privacy policy",
        quota = "uploads used",
        quotaUnlimited = "Unlimited uploads",
        uploads = "Uploads",
        emptyTitle = "No job descriptions yet",
        emptyMessage = "Upload a PDF or Word document, or paste a posting URL, and we build the mix for you.",
        openMix = "Open mix",
        viewFile = "View file",
        filePreview = WMFilePreviewStrings(
            hint = "Word documents are shown as the PDF we read. If the file doesn't appear, download it to open it.",
            frameTitle = "Preview of",
            close = "Close",
            download = "Download"
        ),
        fileUnavailableTitle = "File unavailable",
        fileUnavailableMessage = "The file you uploaded for this mix is no longer stored, so it can't be shown.",
        reevaluate = "Re-evaluate",
        notJobDescriptionTitle = "This isn't a job description",
        pageUnavailableTitle = "We couldn't open that posting",
        maliciousTitle = "This file was rejected for safety",
        analysisFailedTitle = "The analysis didn't finish",
        remedyPrintToPdf = "To add it anyway, open the posting in your browser and print the page (Ctrl+P, or ⌘P on a " +
            "Mac). Choose \"Save as PDF\" as the destination instead of a printer, then upload that PDF here.",
        remedyUploadTheJobDescription = "Upload the job description itself, as a PDF or Word file.",
        remedyMalicious = "Nothing from it was kept. If you think this is a mistake, save the job description as a new " +
            "PDF and upload that.",
        remedyRetry = "Use Retry on the upload to run it again.",
        notCounted = "This didn't count against your uploads.",
        retryUpload = "Retry",
        retryAtLimit = { quota ->
            "You've used your plan's $quota uploads. Upgrade to Pro to retry this one, or delete it."
        },
        deleteUpload = "Delete",
        deleteUploadTitle = "Delete this upload?",
        deleteUploadMessage = { name -> "$name and its file will be deleted. This can't be undone." },
        deleteUploadFailedTitle = "The upload couldn't be deleted",
        processing = "Reading the job description…",
        overQuotaTitle = "Upload limit reached",
        overQuotaMessage = "The Basic plan includes three job description uploads. Upgrade to Pro for unlimited uploads.",
        dropzone = WMUploadDropzoneStrings(dropHere = "Drop a PDF or Word file here", browseFiles = "browse files", hint = "PDF or DOCX, up to 25 MB"),
        row = WMUploadRowStrings(processing = "Analyzing…", ready = "Mix ready", failed = "Failed", rejected = "Rejected"),
        limitTitle = "Upload limit reached",
        limitMessage = "Upgrade to Pro for unlimited job description uploads.",
        invalidFileTitle = "That file can't be used",
        unsupportedType = "Upload a PDF or a Word (.docx) document.",
        tooLarge = "Files must be 25 MB or smaller.",
        emptyFile = "The file is empty.",
        invalidUrlTitle = "That URL can't be used",
        urlBlank = "Paste the URL of a job posting.",
        urlMalformed = "That doesn't look like a URL.",
        urlUnsupported = "That job board isn't supported yet. Email support@wagemix.ai to request it.",
        failedTitle = "The job description couldn't be processed",
        failedMessage = "Please try again or upload a different file.",
        reevaluateTitle = "Re-evaluate job description?",
        reevaluateDescription = "The mix built from this job description will be replaced."
    ),
    share = WMWebStrings.Share(
        title = "Share mix",
        description = "Anyone with the link sees the pay range and the occupations, without an account.",
        payRange = "Pay range (optional)",
        payRangeHelper = "Add the posting's range to show a pay badge.",
        low = "Low",
        high = "High",
        lowAboveHigh = "The low end must not exceed the high end.",
        notPositive = "Enter amounts above zero.",
        badgePreview = "Badge preview",
        noBadge = "No badge: the range midpoint is below the 10th percentile.",
        statsLoading = "Loading the mix's wages…",
        forJobPosting = "Sharing on a job posting",
        create = "Create link",
        linkReady = "Your link is ready",
        linkReadyDescription = "Copy it into a posting, an email or a chat.",
        copy = "Copy link",
        copied = "Copied",
        openLink = "Open",
        previewLoading = "Rendering the preview image…",
        previewImage = "Link preview image",
        previewFailedTitle = "The preview image didn't render",
        previewFailedMessage = "The link works. Regenerate the image that shows when the link is posted.",
        regeneratePreview = "Regenerate preview",
        regenerateFailedTitle = "Couldn't regenerate the preview",
        regenerateFailedMessage = "Try again in a moment.",
        shareAnother = "Share another way",
        failedTitle = "The link couldn't be created",
        failedMessage = "Please try again.",
        sessionExpiredTitle = "Your session expired",
        sessionExpiredMessage = "Log in again to share this mix.",
        tierName = { badge -> when (badge) { WMBadge.None -> "No badge"; WMBadge.Bronze -> "Bronze"; WMBadge.Silver -> "Silver"; WMBadge.Gold -> "Gold"; WMBadge.Platinum -> "Platinum"; WMBadge.Diamond -> "Diamond" } },
        tierExplanation = { badge ->
            when (badge) {
                WMBadge.None -> "Range midpoint is below the 10th percentile"
                WMBadge.Bronze -> "Range midpoint above the 10th percentile"
                WMBadge.Silver -> "Range midpoint above the 25th percentile"
                WMBadge.Gold -> "Range midpoint above the median"
                WMBadge.Platinum -> "Range midpoint above the 75th percentile"
                WMBadge.Diamond -> "Range midpoint above the 90th percentile"
            }
        }
    ),
    account = WMWebStrings.Account(
        title = "Account",
        profile = "Profile",
        firstName = "First name",
        lastName = "Last name",
        email = "Email",
        verified = "Verified",
        unverified = "Not verified",
        saveProfile = "Save changes",
        password = "Password",
        sendResetLink = "Send password reset link",
        resetLinkSent = "Check your email for a link to choose a new password.",
        subscription = "Subscription",
        plan = "Plan",
        renews = "Renews",
        endsOn = "Ends on",
        manageBilling = "Manage billing",
        billingByStripe = "Billing is handled securely by Stripe.",
        firstNameTooLong = { limit, length -> "First name can be at most $limit characters (this one is $length)." },
        lastNameTooLong = { limit, length -> "Last name can be at most $limit characters (this one is $length)." },
        noSubscription = "You're on the free plan.",
        uploads = "Job description uploads",
        profileSavedTitle = "Profile saved",
        profileSavedMessage = "Your name has been updated."
    ),
    pricing = WMWebStrings.Pricing(
        title = "Plans",
        subtitle = "Start free. Upgrade when you need adjustable weights, filters and job-description mixes.",
        perYear = "per year",
        currentPlan = "Current plan",
        choose = "Choose",
        startFree = "Start free",
        freeDescription = "Explore pay ranges for any mix of occupations.",
        basicDescription = "Weight your mix, filter by area and industry, and build three mixes from job descriptions.",
        proDescription = "Everything in Basic with unlimited job description uploads.",
        freeFeatures = listOf("Percentile pay ranges", "Projection to the current year", "Equal-weight mixes"),
        basicFeatures = listOf("Everything in Free", "Adjust and lock weights", "Area and industry filters", "3 job description uploads", "Save and share mixes"),
        proFeatures = listOf("Everything in Basic", "Unlimited job description uploads", "Priority support"),
        popular = "Most popular",
        yourPlan = "Your plan",
        renewNoticeTitle = { plan -> "Your $plan subscription has ended" },
        renewNoticeMessage = "Renew it to get your saved mixes, uploads and share links back. Nothing was deleted.",
        compareTitle = "Compare plans",
        featureColumn = "Feature",
        comparison = listOf(
            "Occupation mixes and band chart" to listOf("✓", "✓", "✓"),
            "Save mixes" to listOf("", "✓", "✓"),
            "Adjust occupation weights" to listOf("", "✓", "✓"),
            "Area and industry filters" to listOf("", "✓", "✓"),
            "AI mixes from job descriptions" to listOf("", "3", "Unlimited"),
            "Share links with pay-range badges" to listOf("", "✓", "✓"),
            "Re-evaluate mixes with AI" to listOf("", "✓", "✓")
        ),
        faqTitle = "Questions",
        faq = listOf(
            "Where does the data come from?" to "Every figure is computed from the U.S. Bureau of Labor Statistics Occupational Employment and Wage Statistics release. Nothing is estimated from job postings.",
            "Can I cancel any time?" to "Yes. Manage or cancel from your account; access continues until the end of the billing period.",
            "What counts as an upload?" to "Each job description you upload or paste as a URL counts once, including re-evaluations."
        ),
        signInTitle = "Log in to subscribe",
        signInMessage = "Create a free account or log in to choose a plan.",
        verifyTitle = "Verify your email first",
        verifyMessage = "We sent a verification link when you registered. Verify your address to subscribe.",
        resend = "Resend link",
        sentTitle = "Verification sent",
        sentMessage = "Check your inbox for the link."
    ),
    payment = WMWebStrings.Payment(
        title = "You're all set",
        thanks = "Thanks for subscribing to Wagemix.",
        plan = "Plan",
        billingCycle = "Billing cycle",
        nextPayment = "Next payment",
        goToMix = "Go to the mix builder",
        failedTitle = "We couldn't confirm the payment",
        failedMessage = "If you completed checkout, your plan will appear in your account shortly."
    ),
    auth = WMWebStrings.Auth(
        showPassword = "Show password",
        hidePassword = "Hide password",
        loginTitle = "Welcome back",
        loginSubtitle = "Log in to your Wagemix account.",
        registerTitle = "Create your account",
        registerSubtitle = "Free forever. Upgrade only when you need more.",
        forgotTitle = "Reset your password",
        forgotSubtitle = "Enter your account email and we'll send you a link to choose a new password.",
        checkEmailTitle = "Check your email",
        checkEmailMessage = "If an account exists for that address, a reset link is on its way.",
        resetTitle = "Choose a new password",
        resetSubtitle = "For",
        resetDoneTitle = "Password changed",
        resetDoneMessage = "Log in with your new password.",
        verifiedTitle = "Email verified",
        verifiedMessage = "You can now subscribe and share mixes.",
        verifyingTitle = "Verifying…",
        firstName = "First name",
        lastName = "Last name",
        email = "Email",
        password = "Password",
        confirmPassword = "Confirm password",
        forgotPassword = "Forgot password?",
        noAccount = "Don't have an account?",
        haveAccount = "Already have an account?",
        backToLogin = "Back to login",
        continueLabel = "Continue",
        sendLink = "Send reset link",
        setPassword = "Set new password",
        brandTitle = "Pay ranges you can defend.",
        brandMessage = "Every number comes from the U.S. Bureau of Labor Statistics.",
        brandHeadline = "The real pay range for any job, straight from BLS data.",
        brandLead = "Blend official BLS occupations into one weighted mix and see the median, middle 50% and typical range.",
        previewTitle = "Preschool Program Director",
        previewBadge = "AI mix",
        createOne = "Create one",
        rememberedIt = "Remembered it?",
        resendEmail = "Resend email",
        resetLinkSentMessage = { email -> "If an account exists for $email, we've sent it a password reset link. Check your inbox and spam folder." },
        newPassword = "New password",
        confirmNewPassword = "Confirm new password",
        passwordRules = "At least 8 characters, with a number, an uppercase and a lowercase letter.",
        consentPrefix = "By creating an account you agree to the ",
        consentJoin = " and ",
        termsOfUse = "Terms of Use",
        privacyPolicy = "Privacy Policy",
        copyright = "© 2026 Wagemix",
        nameBlank = "Required",
        tooLong = { field, limit, length -> "$field can be at most $limit characters (this one is $length)." },
        emailBlank = "Enter your email",
        emailInvalid = "Enter a valid email address",
        passwordBlank = "Enter a password",
        passwordTooShort = "At least 8 characters",
        passwordNoDigit = "Include a digit",
        passwordNoUppercase = "Include an uppercase letter",
        passwordNoLowercase = "Include a lowercase letter",
        passwordMismatch = "Passwords don't match",
        failedTitle = "Something went wrong",
        failed = { reason ->
            when (reason) {
                WMAuthFailure.InvalidCredentials -> "The email or password is incorrect."
                WMAuthFailure.AccountNotFound -> "No account exists for that email."
                WMAuthFailure.UserAlreadyExists -> "An account already exists for that email. Log in instead."
                WMAuthFailure.WeakPassword -> "Choose a stronger password."
                WMAuthFailure.InvalidEmail -> "That email address isn't valid."
                WMAuthFailure.TooManyAttempts -> "Too many attempts. Wait a moment and try again."
                WMAuthFailure.ExpiredLink -> "That link has expired. Request a new one."
                WMAuthFailure.InvalidLink -> "That link isn't valid. Request a new one."
                WMAuthFailure.Network -> "Check your connection and try again."
                WMAuthFailure.Unknown -> "Please try again."
            }
        },
        notVerifiedTitle = "Verify your email",
        notVerifiedMessage = "We sent you a verification link. Verify your address to continue.",
        resend = "Resend link"
    ),
    sharedMix = WMWebStrings.SharedMix(
        wageAnalysis = "Wage analysis",
        badgeIntro = "This posting's pay range earns",
        payRange = "Posted range",
        occupations = "Occupations in this mix",
        aboutTitle = "About this data",
        aboutText = "Wages are weighted across the occupations below using U.S. Bureau of Labor Statistics data, projected to the current year.",
        ctaTitle = "Build your own mix",
        ctaMessage = "Price any role from public wage data, free.",
        ctaButton = "Try Wagemix",
        invalidTitle = "This link isn't valid",
        invalidMessage = "Check the address and try again.",
        notFoundTitle = "This mix isn't available",
        notFoundMessage = "It may have been deleted by its owner.",
        deactivatedTitle = "This link has been deactivated",
        deactivatedMessage = "The person who shared it turned it off. Ask them for a new link if you still need it.",
        failedTitle = "Something went wrong",
        failedMessage = "Please try again later.",
        sharedBy = "Shared with Wagemix"
    ),
    sharedLinks = WMWebStrings.SharedLinks(
        title = "Shared links",
        emptyTitle = "No shared links yet",
        emptyMessage = "Share a saved mix to get a link you can post or send. Every link you create appears here.",
        sharedOn = { date -> "Shared $date" },
        jobPosting = "Job posting",
        social = "Social",
        copyLink = "Copy link",
        copied = "Copied",
        openLink = "Open",
        linkSwitchLabel = { title -> "Link to $title" },
        linkOn = "Link on",
        linkOff = "Link off: visitors see that it was deactivated",
        opens = { count, whileOff ->
            val total = if (count == 1L) "1 open" else "$count opens"
            if (whileOff > 0) "$total ($whileOff while off)" else total
        },
        lastOpened = { dateTime -> "Last opened $dateTime" },
        notOpenedYet = "Not opened yet",
        statsLocked = "Upgrade to Pro to see how often this link was opened.",
        statsUntracked = "Shared before link statistics were added, so this link has none.",
        activationFailedTitle = "The link couldn't be changed",
        activationFailedMessage = "Please try again.",
    ),
    renewal = WMWebStrings.Renewal(
        bannerTitle = "Your subscription has ended",
        bannerMessage = "Renew to get your saved mixes, uploads and share links back. Nothing was deleted.",
        renew = "Renew subscription",
        planEnded = "Ended",
        planEndedText = "Your plan has ended. Renew to get your mixes, uploads and share links back.",
        planRenew = "Renew",
        planEndedTextOf = { plan ->
            "Your $plan plan has ended. Renew to get your mixes, uploads and share links back."
        },
        renewPlan = { plan -> "Renew $plan" },
        sidebarMixesLocked = "Your mixes · renew to see them",
        savedMixesTitle = "Your saved mixes are waiting",
        savedMixesMessage = "Renew your subscription to see, open and share your saved mixes again. Nothing was deleted.",
        uploadsTitle = "Your uploads are waiting",
        uploadsMessage = "Renew your subscription to see your uploads and the mixes made from them, and to analyze new " +
            "job descriptions. Nothing was deleted.",
        sharedLinksTitle = "Your share links are waiting",
        sharedLinksMessage = "Renew your subscription to manage your share links. They stay turned off for the people " +
            "you sent them to until you renew. Nothing was deleted.",
        dialogTitle = "Your subscription has ended",
        dialogMessage = "Saving, sharing and job description uploads come back when you renew. Your saved mixes, " +
            "uploads and share links are kept until then.",
        chartLockedTitle = { filter -> "This mix is filtered to $filter" },
        chartLockedMessage = "Area and industry filters are part of Basic and Pro. Renew to see this chart, or clear " +
            "the filter to see wages across all areas and industries.",
        chartLockedUpgradeMessage = "Area and industry filters are part of Basic and Pro. Upgrade to see this chart, " +
            "or clear the filter to see wages across all areas and industries.",
        clearFilter = "Clear the filter",
    )
)
